<!--
  Shows exactly which leads a "Run AI calls" click placed vs skipped, and WHY
  each skip happened -- before this, the button only ever reported aggregate
  counts ("Placed 16, skipped 27"), leaving admins with no way to tell a
  legitimate skip (already contacted, cooldown, daily cap) from something
  that looked like it should have gone out. Backed by runTaskAICalls'
  placed_details/skipped_details (capped server-side -- see
  DIAL_RUN_DETAIL_CAP in index.mjs -- so a very large campaign shows a
  representative sample here, not a full audit; the Dial Attempts tab is
  the exhaustive record).
-->
<template>
  <v-dialog
    :model-value="modelValue"
    max-width="640"
    scrollable
    @update:model-value="onDialogUpdate"
  >
    <v-card rounded="xl" class="user-dialog-card racr-dialog">
      <div class="dialog-header racr-dialog__header d-flex align-center justify-space-between">
        <div class="d-flex align-center ga-3 min-w-0">
          <div class="racr-dialog__header-icon">
            <v-icon color="primary" size="20">mdi-phone-forward-outline</v-icon>
          </div>
          <div class="min-w-0">
            <span class="dialog-header__title text-truncate d-block">Run AI calls -- results</span>
            <span class="racr-dialog__subtitle text-truncate d-block">{{ headerSubtitle }}</span>
          </div>
        </div>
        <v-btn icon variant="text" size="small" rounded="lg" aria-label="Close" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div v-if="continuing" class="racr-dialog__banner">
        <v-icon size="16" color="primary">mdi-timer-sand</v-icon>
        <span>This is a large campaign -- still working through the rest of the list in the background. This shows the first batch only.</span>
      </div>

      <v-tabs v-model="activeTab" class="racr-dialog__tabs" density="comfortable">
        <v-tab value="placed" class="text-none">
          <v-icon size="16" class="mr-1" color="success">mdi-phone-check-outline</v-icon>
          Placed ({{ placed.length }})
        </v-tab>
        <v-tab value="skipped" class="text-none">
          <v-icon size="16" class="mr-1" color="warning">mdi-phone-off-outline</v-icon>
          Skipped ({{ skipped.length }})
        </v-tab>
      </v-tabs>

      <v-card-text class="racr-dialog__body">
        <v-window v-model="activeTab">
          <v-window-item value="placed">
            <div v-if="placed.length" class="racr-list">
              <div v-for="row in placed" :key="row.request_id" class="racr-row">
                <v-icon size="16" color="success" class="racr-row__icon">mdi-phone-check-outline</v-icon>
                <div class="racr-row__body">
                  <span class="racr-row__name">{{ row.name || 'Unnamed lead' }}</span>
                  <span class="racr-row__number">{{ row.number || '—' }}</span>
                </div>
              </div>
            </div>
            <div v-else class="racr-empty">
              <v-icon size="32" color="primary" class="mb-2">mdi-phone-off-outline</v-icon>
              <p class="racr-empty__text">No calls were placed this run.</p>
            </div>
          </v-window-item>

          <v-window-item value="skipped">
            <div v-if="skipped.length" class="racr-list">
              <div v-for="row in skipped" :key="row.request_id" class="racr-row">
                <v-icon size="16" color="warning" class="racr-row__icon">mdi-phone-off-outline</v-icon>
                <div class="racr-row__body">
                  <div class="racr-row__line1">
                    <span class="racr-row__name">{{ row.name || 'Unnamed lead' }}</span>
                    <span class="racr-row__number">{{ row.number || '—' }}</span>
                  </div>
                  <span class="racr-row__reason">{{ row.reason }}</span>
                </div>
              </div>
            </div>
            <div v-else class="racr-empty">
              <v-icon size="32" color="success" class="mb-2">mdi-check-circle-outline</v-icon>
              <p class="racr-empty__text">Nothing was skipped -- every eligible lead was called.</p>
            </div>
          </v-window-item>
        </v-window>
      </v-card-text>

      <v-card-actions class="dialog-footer racr-dialog__footer">
        <v-spacer />
        <v-btn variant="text" class="text-none dialog-btn dialog-btn--muted" rounded="lg" @click="close">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

defineOptions({ name: 'RunAiCallsResultDialog' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  result: { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue'])

const activeTab = ref('placed')

const placed = computed(() => props.result?.data?.placed_details ?? [])
const skipped = computed(() => props.result?.data?.skipped_details ?? [])
const continuing = computed(() => !!props.result?.data?.continuing)

const headerSubtitle = computed(() => {
  const p = props.result?.data?.placed ?? placed.value.length
  const s = props.result?.data?.skipped ?? skipped.value.length
  return `${p} placed · ${s} skipped`
})

// Land on whichever tab actually has something worth looking at -- a run
// that placed nothing but skipped everyone should open straight to the
// skipped list, not an empty "Placed" tab the admin has to click past.
watch(
  () => props.modelValue,
  (open) => {
    if (open) activeTab.value = placed.value.length ? 'placed' : 'skipped'
  },
)

function onDialogUpdate(value) {
  emit('update:modelValue', value)
}
function close() {
  emit('update:modelValue', false)
}
</script>

<style scoped>
.racr-dialog__header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.1);
  flex-shrink: 0;
}

.racr-dialog__subtitle {
  font-size: var(--y-font-size-xs);
  color: var(--y-text-muted);
  margin-top: 2px;
}

.racr-dialog__banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 20px 4px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.08);
  font-size: 0.78125rem;
  color: var(--y-text-muted);
  line-height: 1.4;
}

.racr-dialog__tabs {
  padding: 0 12px;
  border-bottom: 1px solid var(--y-glass-border);
}

.racr-dialog__body {
  padding: 0 !important;
  max-height: min(52vh, 420px);
  overflow-y: auto;
}

.racr-list {
  padding: 8px 12px 16px;
}

.racr-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 8px;
  border-radius: 10px;
}

.racr-row + .racr-row {
  border-top: 1px solid var(--y-glass-border);
}

.racr-row__icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.racr-row__body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.racr-row__line1 {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.racr-row__name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--y-text);
}

.racr-row__number {
  font-size: 0.75rem;
  color: var(--y-text-muted);
  font-variant-numeric: tabular-nums;
}

.racr-row__reason {
  font-size: 0.75rem;
  color: var(--y-text-muted);
}

.racr-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 24px 36px;
}

.racr-empty__text {
  font-size: 0.8125rem;
  color: var(--y-text-muted);
  margin: 0;
}

.racr-dialog__footer {
  padding-top: 8px !important;
}
</style>
